// BoardViewCard.jsx

import React, { useCallback, useRef } from 'react';

// CSS
import './BoardViewCard.css';

// Components
import BoardViewCardFields from 'views/board/components/BoardViewCardFields';
import { writeNodeDragData } from 'views/dragDrop/nodeDragData';
/**
 * BoardViewCard
 *
 * @returns {ReactNode} The rendered component.
 */
const BoardViewCard = ({
    node,
    index,
    columnKey,
    isDropColumn,
    draggingNodeKey,
    dropPosition,
    theme,
    handleCardClick,
    handleCardDoubleClick,
    handleCardDragStart,
    handleCardDragEnd,
    handleCardDragOver,
    handleCardDrop,
    getCoverUrl,
    CoverComponent,
    cardFieldKeys,
    schemaFieldMap,
    schemaFieldMapLower,
    resolveFieldValue,
    formatFieldValue,
    language,
    nodeDragEnabled = false,
    typeDef = null,
}) => {

    // ----- REFS ----- //

    const suppressNextClickRef = useRef(false);
    const canBoardMove = Boolean(isDropColumn);
    const isDraggable = canBoardMove || Boolean(nodeDragEnabled);

    // ----- MEMOS ----- //

    const onCardClick = useCallback((event) => {
        if (suppressNextClickRef.current) {
            suppressNextClickRef.current = false;
            event?.preventDefault?.();
            event?.stopPropagation?.();
            return;
        }
        handleCardClick(event, node);
    }, [handleCardClick, node]);

    const onCardDoubleClick = useCallback((event) => {
        if (suppressNextClickRef.current) {
            suppressNextClickRef.current = false;
            event?.preventDefault?.();
            event?.stopPropagation?.();
            return;
        }
        handleCardDoubleClick(event, node);
    }, [handleCardDoubleClick, node]);

    const onCardDragStart = useCallback((event) => {
        suppressNextClickRef.current = true;
        if (nodeDragEnabled && !canBoardMove) {
            // Still allow cross-view node drag from non-movable "Other" / mirrored cards.
            writeNodeDragData(event, node);
            event.dataTransfer.effectAllowed = 'copy';
            return;
        }
        if (!canBoardMove) return;
        handleCardDragStart(node, columnKey, event);
    }, [canBoardMove, columnKey, handleCardDragStart, node, nodeDragEnabled]);

    const onCardDragEnd = useCallback(() => {
        handleCardDragEnd();
        setTimeout(() => {
            suppressNextClickRef.current = false;
        }, 0);
    }, [handleCardDragEnd]);

    // ----- RENDER ----- //

    return (
        <div
          className={`board-view-card${draggingNodeKey === node?.key ? ' is-dragging' : ''}${dropPosition ? ` is-drop-${dropPosition}` : ''}`}
          style={{
                backgroundColor: theme.panel,
                borderColor: theme.backgroundHover,
                color: theme.foreground,
            }}
          draggable={isDraggable}
          aria-label={isDraggable ? 'Draggable card' : undefined}
          onClick={onCardClick}
          onDoubleClick={onCardDoubleClick}
          onDragEnd={onCardDragEnd}
          onDragOver={(event) => handleCardDragOver(index, event)}
          onDragStart={onCardDragStart}
          onDrop={(event) => handleCardDrop(index, event)}
        >
            <CoverComponent
              node={node}
              coverUrl={getCoverUrl(node)}
            />
            {/* CARD HEADER */}
            <div className="board-view-card-header">
                {/* CARD TITLE */}
                <div className="board-view-card-title">
                    {node?.title || 'Untitled'}
                </div>
            </div>
            <BoardViewCardFields
              node={node}
              cardFieldKeys={cardFieldKeys}
              schemaFieldMap={schemaFieldMap}
              schemaFieldMapLower={schemaFieldMapLower}
              resolveFieldValue={resolveFieldValue}
              formatFieldValue={formatFieldValue}
              language={language}
              theme={theme}
            />
        </div>
    );
};

// Dev Tools
BoardViewCard.displayName = 'BoardViewCard';

export default React.memo(BoardViewCard);
